import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: inline-block;
      vertical-align: middle;
    }
    :host([fluid]) { display: block; width: 100%; }

    /* ── Shared custom properties ──────────────────────────────────────────────── */
    .uwc-in {
      --_bg:        var(--uwc-inputnumber-bg,                ${t.surface});
      --_border:    var(--uwc-inputnumber-border,            1px solid ${t.borderControl});
      --_radius:    var(--uwc-inputnumber-radius,            ${t.radiusMd});
      --_color:     var(--uwc-inputnumber-color,             ${t.text});
      --_ph-color:  var(--uwc-inputnumber-placeholder-color, ${t.textMuted});
      --_font-size: var(--uwc-inputnumber-font-size,         ${t.fontSizeMd});
      --_px:        var(--uwc-inputnumber-padding-x,         0.75rem);
      --_py:        var(--uwc-inputnumber-padding-y,         0);
      --_btn-w:     var(--uwc-inputnumber-button-width,      2.25rem);

      position:      relative;
      display:       flex;
      align-items:   stretch;
      width:         100%;
      min-height:    var(--uwc-inputnumber-height, ${t.controlHeightMd});
      background:    var(--_bg);
      border:        var(--_border);
      border-radius: var(--_radius);
      transition:    border-color ${t.durationBase}, box-shadow ${t.durationBase};
    }

    .uwc-in:focus-within {
      border-color: var(--uwc-color-primary, ${t.primary});
      box-shadow: 0 0 0 3px
        color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 20%, transparent);
    }

    /* ── Variants ───────────────────────────────────────────────────────────────── */
    .uwc-in--filled {
      --_bg: var(--uwc-inputnumber-filled-bg, ${t.surfaceRaised});
      border-color: transparent;
    }
    .uwc-in--filled:focus-within {
      --_bg: var(--uwc-inputnumber-bg, ${t.surface});
      border-color: var(--uwc-color-primary, ${t.primary});
    }

    /* ── Invalid ────────────────────────────────────────────────────────────────── */
    .uwc-in--invalid { border-color: var(--uwc-color-danger, ${t.danger}) !important; }
    .uwc-in--invalid:focus-within {
      box-shadow: 0 0 0 3px
        color-mix(in oklab, var(--uwc-color-danger, ${t.danger}) 20%, transparent);
    }

    /* ── Sizes ──────────────────────────────────────────────────────────────────── */
    .uwc-in--small {
      --_font-size: ${t.fontSizeXs};
      --_px:        0.5rem;
      --_py:        0;
      --_btn-w:     1.75rem;
      min-height:   ${t.controlHeightSm};
    }
    .uwc-in--large {
      --_font-size: ${t.fontSizeLg};
      --_px:        1rem;
      --_py:        0;
      --_btn-w:     2.75rem;
      min-height:   ${t.controlHeightLg};
    }

    /* ── Disabled ───────────────────────────────────────────────────────────────── */
    .uwc-in--disabled { pointer-events: none; opacity: 0.6; }

    /* ── Field (input + floating label container) ───────────────────────────────── */
    .uwc-in__field {
      flex:     1;
      min-width: 0;
      position: relative;
      display:  flex;
      align-items: stretch;
    }

    /* ── Native input ───────────────────────────────────────────────────────────── */
    input {
      flex:            1;
      min-width:       0;
      padding-inline:  var(--_px);
      padding-block:   var(--_py);
      font-family:     var(--uwc-font-family, inherit);
      font-size:       var(--_font-size);
      color:           var(--_color);
      background:      transparent;
      border:          none;
      outline:         none;
      text-align:      var(--uwc-inputnumber-text-align, left);
    }
    input::placeholder  { color: var(--_ph-color); }
    input:disabled      { cursor: not-allowed; }

    /* ── Spinner buttons (shared) ───────────────────────────────────────────────── */
    .uwc-in__btn {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      flex-shrink:     0;
      width:           var(--_btn-w);
      padding:         0;
      background:      var(--uwc-inputnumber-btn-bg, transparent);
      color:           var(--uwc-inputnumber-btn-color, ${t.textSecondary});
      border:          none;
      cursor:          pointer;
      outline:         none;
      user-select:     none;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color      ${t.durationFast} ${t.easingStandard};
    }
    .uwc-in__btn:hover:not([disabled]) {
      background: var(--uwc-inputnumber-btn-hover-bg,
        color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 8%, transparent));
      color: var(--uwc-inputnumber-btn-hover-color,
        var(--uwc-color-primary, ${t.primary}));
    }
    .uwc-in__btn:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }
    .uwc-in__btn[disabled] { cursor: not-allowed; opacity: 0.5; }
    .uwc-in__btn uwc-icon { pointer-events: none; }

    /* ── Stacked layout ─────────────────────────────────────────────────────────── */
    .uwc-in--stacked .uwc-in__stacked-btns {
      display:             flex;
      flex-direction:      column;
      border-top-right-radius:    calc(var(--_radius) - 1px);
      border-bottom-right-radius: calc(var(--_radius) - 1px);
      overflow:            hidden;
    }
    .uwc-in--stacked .uwc-in__btn {
      flex:  1;
      width: var(--_btn-w);
    }

    /* ── Horizontal layout ──────────────────────────────────────────────────────── */
    .uwc-in--horizontal .uwc-in__btn {
      align-self: stretch;
    }
    .uwc-in--horizontal .uwc-in__btn--dec {
      border-inline-end:          var(--_border);
      border-top-left-radius:     calc(var(--_radius) - 1px);
      border-bottom-left-radius:  calc(var(--_radius) - 1px);
      overflow:                   hidden;
    }
    .uwc-in--horizontal .uwc-in__btn--inc {
      border-inline-start:        var(--_border);
      border-top-right-radius:    calc(var(--_radius) - 1px);
      border-bottom-right-radius: calc(var(--_radius) - 1px);
      overflow:                   hidden;
    }
    .uwc-in--horizontal input { text-align: center; }

    /* ── Vertical layout ────────────────────────────────────────────────────────── */
    .uwc-in--vertical {
      flex-direction: column;
      min-height: auto;
    }
    .uwc-in--vertical .uwc-in__btn {
      width:  100%;
      height: var(--_btn-w);
    }
    .uwc-in--vertical .uwc-in__btn--inc {
      border-block-end:           var(--_border);
      border-top-left-radius:     calc(var(--_radius) - 1px);
      border-top-right-radius:    calc(var(--_radius) - 1px);
      overflow:                   hidden;
    }
    .uwc-in--vertical .uwc-in__btn--dec {
      border-block-start:            var(--_border);
      border-bottom-left-radius:  calc(var(--_radius) - 1px);
      border-bottom-right-radius: calc(var(--_radius) - 1px);
      overflow:                   hidden;
    }
    .uwc-in--vertical input { text-align: center; }

    /* ── Float label ────────────────────────────────────────────────────────────── */
    .uwc-in--float { overflow: visible; }

    .uwc-in__label {
      position:       absolute;
      left:           var(--_px);
      line-height:    1;
      white-space:    nowrap;
      pointer-events: none;
      padding-inline: 0.25rem;
      transition:
        top       ${t.durationBase} ${t.easingStandard},
        font-size ${t.durationBase} ${t.easingStandard},
        color     ${t.durationBase} ${t.easingStandard},
        transform ${t.durationBase} ${t.easingStandard};
    }

    /* float-over: label floats above the border */
    .uwc-in--float-over { margin-top: 1.5rem; }
    .uwc-in--float-over .uwc-in__label {
      top:       50%;
      transform: translateY(-50%);
      font-size: var(--_font-size);
      color:     var(--_ph-color);
    }
    .uwc-in--float-over:focus-within .uwc-in__label,
    .uwc-in--float-over input:not(:placeholder-shown) ~ .uwc-in__label {
      top:       0;
      transform: translateY(calc(-100% - 0.375rem));
      font-size: calc(var(--_font-size) * 0.85);
      color:     var(--uwc-inputnumber-label-active-color,
        var(--uwc-color-primary, ${t.primary}));
    }

    /* float-on: label straddles the top border */
    .uwc-in--float-on { margin-top: 0.75rem; }
    .uwc-in--float-on .uwc-in__label {
      top:        50%;
      transform:  translateY(-50%);
      font-size:  var(--_font-size);
      color:      var(--_ph-color);
      background: var(--_bg);
    }
    .uwc-in--float-on:focus-within .uwc-in__label,
    .uwc-in--float-on input:not(:placeholder-shown) ~ .uwc-in__label {
      top:       0;
      transform: translateY(-50%);
      font-size: calc(var(--_font-size) * 0.8);
      color:     var(--uwc-inputnumber-label-active-color,
        var(--uwc-color-primary, ${t.primary}));
    }

    /* float-in: label lives inside the field, shifts to top on fill */
    .uwc-in--float-in input {
      padding-top:    1.375rem;
      padding-bottom: 0.3125rem;
    }
    .uwc-in--float-in .uwc-in__label {
      top:            50%;
      transform:      translateY(-50%);
      font-size:      var(--_font-size);
      color:          var(--_ph-color);
      background:     transparent;
      padding-inline: 0;
      left:           var(--_px);
    }
    .uwc-in--float-in:focus-within .uwc-in__label,
    .uwc-in--float-in input:not(:placeholder-shown) ~ .uwc-in__label {
      top:            0.3125rem;
      transform:      none;
      font-size:      calc(var(--_font-size) * 0.75);
      color:          var(--uwc-inputnumber-label-active-color,
        var(--uwc-color-primary, ${t.primary}));
      padding-inline: 0;
    }

    /* Invalid overrides label color for all float variants */
    .uwc-in--invalid .uwc-in__label,
    .uwc-in--invalid:focus-within .uwc-in__label,
    .uwc-in--invalid input:not(:placeholder-shown) ~ .uwc-in__label {
      color: var(--uwc-color-danger, ${t.danger});
    }

    /* ── Reduced motion ─────────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-in,
      .uwc-in__btn,
      .uwc-in__label { transition: none; }
    }
  `,
];
